/**
 * Mission run overlay for the COP.
 *
 * A movement that is under way publishes two things the COP cares about: the
 * route it is meant to follow, and the events raised against it. The snapshot
 * covers a COP opened mid-run; the stream covers everything after. Fetch the
 * snapshot first and the two together are continuous.
 */

import { withSseAccessToken } from '@signals/app-shell/utils/sseAuthUrl';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export type MissionEventSeverity = 'critical' | 'warning' | 'info';

export type MissionEventType =
  | 'off_route'
  | 'back_on_route'
  | 'signal_lost'
  | 'signal_restored'
  | 'corridor_threat'
  | 'wifi_located'
  | 'ble_located'
  | 'cell_located'
  | 'watchlist_on_route'
  | 'cotraveler_suspected'
  | 'cotraveler_confirmed'
  | 'uas_intercept'
  | 'surveillance_ahead'
  | 'route_changed'
  | 'threat_stale'
  | 'threat_reacquired';

export interface MissionEvent {
  id: number;
  mission_id: number;
  device_id: string;
  label?: string | null;
  event_type: MissionEventType | string;
  severity: MissionEventSeverity | string;
  threat_key?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  distance_m?: number | null;
  segment_index?: number | null;
  fix_at?: string | null;
  detected_at: string;
  acknowledged_at?: string | null;
  meta?: Record<string, any> | null;
}

export interface MissionRoute {
  mission_id: number;
  name: string;
  status: string;
  profile?: string | null;
  geometry: GeoJSON.LineString;
  started_at?: string | null;
  deviation_threshold_m?: number | null;
  assignees: Array<{ device_id: string; label?: string | null }>;
}

export interface MissionOverlaySnapshot {
  missions: MissionRoute[];
  events: MissionEvent[];
  timestamp: string;
}

/** Events that describe a threat rather than the convoy's own behaviour. */
export const THREAT_EVENT_TYPES = new Set<string>([
  'corridor_threat',
  'watchlist_on_route',
  'cotraveler_suspected',
  'cotraveler_confirmed',
  'uas_intercept',
  'surveillance_ahead',
  'threat_reacquired',
]);

/**
 * Events that retire a threat rather than raise one.
 *
 * The map is a claim about the present, so a threat nothing has observed for a
 * minute has to come off it. The backend decides when that happens and says so
 * with a `threat_stale` event carrying the same `threat_key`; the COP's job is
 * only to stop drawing it.
 */
export const THREAT_CLEARING_TYPES = new Set<string>(['threat_stale']);

export const MISSION_EVENT_LABELS: Record<string, string> = {
  off_route: 'Off route',
  back_on_route: 'Back on route',
  signal_lost: 'Signal lost',
  signal_restored: 'Signal restored',
  corridor_threat: 'Threat in corridor',
  wifi_located: 'Wi-Fi located',
  ble_located: 'BLE located',
  cell_located: 'Cell located',
  watchlist_on_route: 'Watchlist emitter on route',
  cotraveler_suspected: 'Possible co-traveler',
  cotraveler_confirmed: 'Co-traveler confirmed',
  uas_intercept: 'Drone on intercept',
  surveillance_ahead: 'Surveillance ahead',
  route_changed: 'Route changed',
  threat_stale: 'Threat no longer detected',
  threat_reacquired: 'Threat back',
  field_report: 'Field report',
  rf_multiloc: 'Heard in several places',
};

export const missionEventLabel = (type: string): string =>
  MISSION_EVENT_LABELS[type] || type.replace(/_/g, ' ');

export const missionSeverityColor = (severity?: string | null): string => {
  switch ((severity || '').toLowerCase()) {
    case 'critical':
      return '#f44336';
    case 'warning':
      return '#ff9800';
    case 'info':
      return '#2196f3';
    default:
      return '#9e9e9e';
  }
};

export const isThreatEvent = (event: MissionEvent): boolean =>
  THREAT_EVENT_TYPES.has(event.event_type);

export const isThreatClearingEvent = (event: MissionEvent): boolean =>
  THREAT_CLEARING_TYPES.has(event.event_type);

/**
 * The threats still worth drawing, given everything raised so far.
 *
 * Events are an append-only log, so "is this threat still there" is only
 * answerable by replaying it: the newest event carrying a given `threat_key`
 * wins, and if that event is a clearing one the threat is gone. Threats with
 * no key at all — older rows, mostly — are kept as-is, since there is nothing
 * that could ever retire them.
 */
export function activeMissionThreats(events: MissionEvent[]): MissionEvent[] {
  const latestByKey = new Map<string, MissionEvent>();
  const keyless: MissionEvent[] = [];

  for (const event of events) {
    if (!isThreatEvent(event) && !isThreatClearingEvent(event)) continue;
    const key = event.threat_key ? `${event.mission_id}:${event.threat_key}` : null;
    if (!key) {
      if (isThreatEvent(event)) keyless.push(event);
      continue;
    }
    const seen = latestByKey.get(key);
    if (!seen || new Date(event.detected_at) >= new Date(seen.detected_at)) {
      latestByKey.set(key, event);
    }
  }

  const active = Array.from(latestByKey.values()).filter((e) => !isThreatClearingEvent(e));
  return [...active, ...keyless].sort(
    (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime(),
  );
}

export interface MissionPromoteResult {
  watchlist_id: number;
  identifier: string;
  identifier_type: string;
  label: string;
  already_present: boolean;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
});

class MissionEventsService {
  /** Routes under way plus the events already raised against them. */
  async getOverlay(hours = 12): Promise<MissionOverlaySnapshot> {
    const response = await fetch(`${API_BASE}/sse/mission-events/current?hours=${hours}`, {
      method: 'GET',
      headers: authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  async acknowledge(missionId: number, eventId: number): Promise<void> {
    const response = await fetch(
      `${API_BASE}/missions/${missionId}/route-events/ack?event_id=${eventId}`,
      { method: 'POST', headers: authHeaders() },
    );
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  /**
   * Put the emitter behind an event onto the DFP watchlist.
   *
   * Identifier inference stays server-side: the event already knows which
   * field it matched on, and re-deriving that in the browser would be a second
   * copy of a rule that must not drift from the detector's.
   */
  async promoteToWatchlist(
    missionId: number,
    eventId: number,
    reason?: string,
  ): Promise<MissionPromoteResult> {
    const response = await fetch(
      `${API_BASE}/missions/${missionId}/route-events/${eventId}/promote`,
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reason: reason || null }),
      },
    );
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(detail || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /** EventSource URL with the access token attached, since EventSource cannot set headers. */
  streamUrl(missionId?: number | null): string {
    const query = missionId ? `?mission_id=${missionId}` : '';
    return withSseAccessToken(`${API_BASE}/sse/mission-events${query}`);
  }
}

export const missionEventsService = new MissionEventsService();
